// ==========================================
// Buddy threads — the chat history store
// ==========================================
// Implements what 3d already asked for: "Context memory is per-thread; Cmd/Ctrl+J
// switches threads." This service owns the collection; BuddyChatService is a view
// onto whichever thread is active, so the panel and rail never talk to it directly.
//
// It deliberately has no dependencies — it is a store plus persistence, which keeps
// chat -> threads a one-way edge and avoids a cycle with the workflow service.
// ==========================================

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { BuddyMessage, BuddyThread, ThreadGroup, WorkflowRun } from '../models/buddy.types';
import { TITLE_MAX_CHARS } from '../models/buddy.constants';
import { BuddyApiService } from './buddy-api.service';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * ChatGPT titles a chat with the model. Buddy has no model, so the title comes from
 * the first user message: markdown stripped, whitespace collapsed, truncated on a
 * word boundary.
 */
export function deriveTitle(text: string): string {
  const clean = text
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

  if (!clean) {
    return 'New chat';
  }
  if (clean.length <= TITLE_MAX_CHARS) {
    return clean;
  }
  const cut = clean.slice(0, TITLE_MAX_CHARS);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > TITLE_MAX_CHARS / 2 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

/** Midnight of the given date, for whole-day comparisons. */
function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** ChatGPT's exact buckets. */
export function groupLabelFor(timestamp: number, now: Date = new Date()): string {
  const then = new Date(timestamp);
  const days = Math.round((startOfDay(now) - startOfDay(then)) / 86_400_000);

  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return 'Previous 7 Days';
  if (days < 30) return 'Previous 30 Days';
  if (then.getFullYear() === now.getFullYear()) return MONTHS[then.getMonth()];
  return String(then.getFullYear());
}

/** Compact age for a history row: 4m · 3h · Jul 24 · Mar 2025. */
export function relativeTime(timestamp: number, now: Date = new Date()): string {
  const diff = now.getTime() - timestamp;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;

  const then = new Date(timestamp);
  const month = MONTHS[then.getMonth()].slice(0, 3);
  return then.getFullYear() === now.getFullYear()
    ? `${month} ${then.getDate()}`
    : `${month} ${then.getFullYear()}`;
}

/** Group ordering: pinned first, then newest bucket to oldest. */
const FIXED_ORDER = ['Pinned', 'Today', 'Yesterday', 'Previous 7 Days', 'Previous 30 Days'];

@Injectable({ providedIn: 'root' })
export class BuddyThreadsService {
  private readonly threadsSubject: BehaviorSubject<BuddyThread[]>;
  private readonly activeIdSubject: BehaviorSubject<string>;

  readonly threads: Observable<BuddyThread[]>;
  readonly activeId: Observable<string>;
  readonly activeThread: Observable<BuddyThread>;

  constructor(private readonly api: BuddyApiService) {
    this.threadsSubject = new BehaviorSubject<BuddyThread[]>([]);
    this.activeIdSubject = new BehaviorSubject<string>('');

    this.threads = this.threadsSubject.asObservable();
    this.activeId = this.activeIdSubject.pipe(distinctUntilChanged());
    this.activeThread = combineLatest([this.threadsSubject, this.activeIdSubject]).pipe(
      map(([threads, id]) => threads.find((t) => t.id === id) ?? threads[0])
    );

    // Guarantee a visible "New chat" synchronously so activeThread is never
    // undefined during the async load; real threads replace it if any exist.
    this.createThread();
    this.loadFromApi();
  }

  /** GET /api/v1/buddy/threads → the sidebar list; opens the newest thread. */
  private loadFromApi(): void {
    this.api.listThreads().subscribe({
      next: (threads) => {
        if (threads.length > 0) {
          this.commit(threads);
          this.setActive(threads[0].id);
        }
        // No threads yet → keep the empty "New chat" created in the constructor.
      },
      error: () => {
        // API unreachable → keep the empty "New chat" so the panel still works.
      },
    });
  }

  // ------------------------------------------
  // Reads
  // ------------------------------------------

  get all(): BuddyThread[] {
    return this.threadsSubject.value;
  }

  get activeThreadId(): string {
    return this.activeIdSubject.value;
  }

  get active(): BuddyThread {
    return this.byId(this.activeThreadId) ?? this.threadsSubject.value[0];
  }

  byId(id: string): BuddyThread | undefined {
    return this.threadsSubject.value.find((t) => t.id === id);
  }

  /**
   * The drawer's list. Search matches title *and* message text, as ChatGPT's does.
   * Archived threads are excluded unless explicitly asked for.
   */
  groups(query = '', archived = false): ThreadGroup[] {
    const q = query.trim().toLowerCase();
    const now = new Date();

    const matching = this.threadsSubject.value
      .filter((t) => t.archived === archived)
      .filter((t) => {
        if (!q) return true;
        if (t.title.toLowerCase().includes(q)) return true;
        return t.messages.some((m) => m.text.toLowerCase().includes(q));
      })
      .sort((a, b) => b.updatedAt - a.updatedAt);

    const buckets = new Map<string, BuddyThread[]>();
    for (const thread of matching) {
      const label = !archived && thread.pinned ? 'Pinned' : groupLabelFor(thread.updatedAt, now);
      const list = buckets.get(label);
      if (list) {
        list.push(thread);
      } else {
        buckets.set(label, [thread]);
      }
    }

    return [...buckets.entries()]
      .map(([label, threads]) => ({ label, threads }))
      .sort((a, b) => this.groupRank(a) - this.groupRank(b));
  }

  archivedCount(): number {
    return this.threadsSubject.value.filter((t) => t.archived).length;
  }

  /** Rank groups so fixed buckets lead and month/year buckets fall in date order. */
  private groupRank(group: ThreadGroup): number {
    const fixed = FIXED_ORDER.indexOf(group.label);
    if (fixed !== -1) {
      return fixed;
    }
    // Everything else sorts by its newest thread, newest first.
    const newest = group.threads.length ? group.threads[0].updatedAt : 0;
    return FIXED_ORDER.length + (Number.MAX_SAFE_INTEGER - newest) / 1e13;
  }

  /** The first line of the newest message, used as the row's preview when searching. */
  snippetFor(thread: BuddyThread, query: string): string | null {
    const q = query.trim().toLowerCase();
    if (!q) {
      return null;
    }
    const hit = thread.messages.find((m) => m.text.toLowerCase().includes(q));
    if (!hit) {
      return null;
    }
    const flat = hit.text.replace(/\s+/g, ' ').trim();
    return flat.length > 60 ? `${flat.slice(0, 60)}…` : flat;
  }

  // ------------------------------------------
  // Mutations
  // ------------------------------------------

  /**
   * "New chat": an empty thread with NO server id yet (id = ''). The real id is
   * issued by the backend on the first message and adopted via adoptThreadId().
   * Only one unsaved "New chat" exists at a time.
   */
  createThread(): string {
    const now = Date.now();
    const thread: BuddyThread = {
      id: '',
      title: 'New chat',
      titleIsCustom: false,
      messages: [],
      runs: {},
      createdAt: now,
      updatedAt: now,
      pinned: false,
      archived: false,
    };
    const withoutEmpty = this.threadsSubject.value.filter((t) => t.id !== '');
    this.commit([thread, ...withoutEmpty]);
    this.activeIdSubject.next('');
    return '';
  }

  setActive(id: string): void {
    const thread = this.byId(id);
    if (!thread) {
      return;
    }
    this.activeIdSubject.next(id);

    // Lazy-load a real thread's messages the first time it is opened.
    if (id && thread.messages.length === 0) {
      this.api.getMessages(id).subscribe({
        next: (messages) => this.patch(id, { messages }),
        error: () => {},
      });
    }
  }

  /**
   * Swap a new chat's placeholder id ('') for the server-issued thread id after
   * the first message. Keeps the thread's messages and makes it active.
   */
  adoptThreadId(oldId: string, newId: string, title?: string): void {
    const next = this.threadsSubject.value.map((t) =>
      t.id === oldId
        ? { ...t, id: newId, ...(title && !t.titleIsCustom ? { title } : {}) }
        : t
    );
    this.commit(next);
    if (this.activeThreadId === oldId) {
      this.activeIdSubject.next(newId);
    }
  }

  setMessages(threadId: string, messages: BuddyMessage[]): void {
    this.patch(threadId, { messages, updatedAt: Date.now() });
  }

  setRuns(threadId: string, runs: Record<string, WorkflowRun>): void {
    this.patch(threadId, { runs, updatedAt: Date.now() });
  }

  /** Applied once, after the first exchange; a rename blocks it permanently. */
  autoTitle(threadId: string): void {
    const thread = this.byId(threadId);
    if (!thread || thread.titleIsCustom) {
      return;
    }
    const firstUser = thread.messages.find((m) => m.role === 'user');
    if (!firstUser) {
      return;
    }
    this.patch(threadId, { title: deriveTitle(firstUser.text) });
  }

  rename(threadId: string, title: string): void {
    const clean = title.trim();
    if (!clean) {
      return;
    }
    this.patch(threadId, { title: clean, titleIsCustom: true });
    if (threadId) {
      this.api.updateThread(threadId, { title: clean }).subscribe({ error: () => {} });
    }
  }

  togglePin(threadId: string): void {
    const thread = this.byId(threadId);
    if (!thread) {
      return;
    }
    const pinned = !thread.pinned;
    this.patch(threadId, { pinned });
    if (threadId) {
      this.api.updateThread(threadId, { pinned }).subscribe({ error: () => {} });
    }
  }

  setArchived(threadId: string, archived: boolean): void {
    this.patch(threadId, { archived });
    if (threadId) {
      this.api.updateThread(threadId, { archived }).subscribe({ error: () => {} });
    }
    // Archiving the open thread moves you to the newest visible one, as ChatGPT does.
    if (archived && threadId === this.activeThreadId) {
      const next = this.threadsSubject.value.find((t) => !t.archived && t.id !== threadId);
      if (next) {
        this.setActive(next.id);
      } else {
        this.createThread();
      }
    }
  }

  remove(threadId: string): void {
    const remaining = this.threadsSubject.value.filter((t) => t.id !== threadId);
    this.commit(remaining);
    if (threadId) {
      this.api.deleteThread(threadId).subscribe({ error: () => {} });
    }

    if (threadId !== this.activeThreadId) {
      return;
    }
    const next = remaining.find((t) => !t.archived);
    if (next) {
      this.setActive(next.id);
    } else {
      // Never leave the panel with no thread to show.
      this.createThread();
    }
  }

  /**
   * Stands in for ChatGPT's Share, which has no destination in an on-prem ERP.
   * Returns the thread as Markdown for the clipboard or a .md download.
   */
  exportMarkdown(threadId: string): string {
    const thread = this.byId(threadId);
    if (!thread) {
      return '';
    }
    const when = new Date(thread.createdAt).toISOString().slice(0, 10);
    const lines = [`# ${thread.title}`, '', `_${when}_`, ''];

    for (const message of thread.messages) {
      if (message.role === 'system') {
        lines.push(`> ${message.text}`, '');
        continue;
      }
      lines.push(`**${message.role === 'user' ? 'You' : 'BB'}:** ${message.text}`, '');
    }
    return lines.join('\n');
  }

  // ------------------------------------------
  // Internals
  // ------------------------------------------

  private patch(threadId: string, patch: Partial<BuddyThread>): void {
    let changed = false;
    const next = this.threadsSubject.value.map((t) => {
      if (t.id !== threadId) {
        return t;
      }
      changed = true;
      return { ...t, ...patch };
    });
    if (changed) {
      this.commit(next);
    }
  }

  /**
   * In-memory commit. The backend is the source of truth (messages are persisted
   * server-side on send; thread mutations go through the API above), so the client
   * copy is transient and not written to localStorage.
   */
  private commit(threads: BuddyThread[]): void {
    this.threadsSubject.next(threads);
  }
}
